import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import EavAttributeField from '../components/EavAttributeField';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { findFirstEavError } from '../utils/attributeValidation';

const SupplierForm = ({ viewOnly = false }) => {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const { success: notifySuccess, error: notifyError } = useNotification();
  const { id } = useParams();
  const [config, setConfig] = useState([]);
  const [defaultAttributeSet, setDefaultAttributeSet] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    attribute_set_id: null, // Will be set to default supplier set
    attributes: {},
  });
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 获取供应商默认属性组
  useEffect(() => {
    const fetchDefaultAttributeSet = async () => {
      try {
        const res = await fetch('/api/settings/attribute-sets');
        const data = await res.json();
        if (data.status === 'success' && data.data) {
          // 查找供应商默认属性组 (set_code='default', entity_type='supplier')
          const defaultSet = data.data.find(
            set => set.entity_type === 'supplier' && set.set_code === 'default'
          );
          if (defaultSet) {
            setDefaultAttributeSet(defaultSet);
            // 更新表单数据中的attribute_set_id
            setFormData(prev => ({
              ...prev,
              attribute_set_id: defaultSet.set_id
            }));
          }
        }
      } catch (err) {
        console.error('Failed to fetch default attribute set:', err);
      }
    };
    fetchDefaultAttributeSet();
  }, []);

  useEffect(() => {
    if (id) {
      fetch(`/api/suppliers/${id}`)
        .then(res => {
          if (!res.ok) {
            throw new Error(`API returned ${res.status}`);
          }
          return res.json();
        })
        .then(res => {
          if (res.status === 'success' && res.data) {
            setFormData({
              ...res.data,
              // 强制使用供应商默认属性组，忽略API返回的attribute_set_id
              attribute_set_id: defaultAttributeSet ? defaultAttributeSet.set_id : res.data.attribute_set_id,
              password: '',
            });
          } else {
            console.error('Supplier API returned error:', res.message);
          }
        })
        .catch(err => {
          console.error('Failed to fetch supplier:', err);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [id, defaultAttributeSet]);

  useEffect(() => {
    if (!formData.attribute_set_id) return;
    fetch(`/api/suppliers/form-config?attribute_set_id=${formData.attribute_set_id}`)
      .then(res => {
        if (!res.ok) {
          throw new Error(`API returned ${res.status}`);
        }
        return res.json();
      })
      .then(res => {
        if (res.status === 'success' && res.data) {
          setConfig(res.data);
        } else {
          console.error('Supplier form config API returned error:', res.message);
          setConfig([]);
        }
      })
      .catch(err => {
        console.error('Failed to fetch supplier form config:', err);
        setConfig([]);
      });
  }, [formData.attribute_set_id]);

  const handleAttributeChange = (code, value) => {
    setFormData(prev => ({
      ...prev,
      attributes: { ...prev.attributes, [code]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (viewOnly) {
      navigate('/suppliers');
      return;
    }
    // 动态验证所有 is_required=1 的EAV属性 + input_validation 格式验证（使用共享工具）
    const eavError = findFirstEavError(config, formData.attributes, t);
    if (eavError) {
      notifyError(eavError);
      return;
    }

    const endpoint = id ? `/api/suppliers/${id}/edit` : '/api/suppliers/add';
    const method = id ? 'PUT' : 'POST';

    setIsSubmitting(true);
    try {
      const res = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.status === 'success') {
        notifySuccess(t('common.success'));
        navigate('/suppliers');
      } else {
        notifyError(t('common.error') + ': ' + data.message);
      }
    } catch (err) {
      notifyError(t('common.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div className="text-center p-10">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{viewOnly ? t('suppliers.view_supplier') : (id ? t('suppliers.edit_supplier') : t('suppliers.new_supplier'))}</h1>
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-200 rounded-full">
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-8">
        <div className={viewOnly ? 'pointer-events-none select-none' : ''}>
        <div className="space-y-8">

          {/* Attribute Set (hidden) */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('suppliers.attr_set')}</label>
            <div className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
              {defaultAttributeSet ? defaultAttributeSet.set_name : t('common.supplier_default_set')}
              <input type="hidden" name="attribute_set_id" value={formData.attribute_set_id || ''} />
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('common.only_default_set', {entity: t('entity.supplier')})}</p>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
              {t('common.email')} <span className="text-slate-400 font-normal">(Optional)</span>
            </label>
            <input
              type="email"
              className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent"
              value={formData.email || ''}
              onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="supplier@example.com"
            />
          </div>

          {/* EAV Attributes - 每行一个，使用 EavAttributeField */}
          {config.length > 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-y-6">
                {config.map(attr => (
                  <EavAttributeField
                    key={attr.attribute_id}
                    attr={attr}
                    value={(formData.attributes || {})[attr.attribute_code] || ''}
                    onChange={handleAttributeChange}
                    viewOnly={viewOnly}
                  />
                ))}
              </div>
            </div>
          )}

        </div>
        </div>

        <div className="flex justify-end gap-3 pt-6 border-t">
          {viewOnly ? (
            <button 
              type="button" 
              onClick={() => navigate(-1)} 
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('common.close')}
            </button>
          ) : (
            <>
              <button 
                type="button" 
                onClick={() => navigate(-1)} 
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button 
                type="submit" 
                disabled={isSubmitting}
                className={`px-6 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('common.processing')}
                  </span>
                ) : (
                  <><Save size={18} />{id ? t('common.update') : t('common.add')}</>
                )}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default SupplierForm;
